import React, { useState } from 'react';
import { Edit2, Info, HeartPulse, Sparkles, ChevronDown, ChevronUp, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
const pulseScoreData = [{
  month: 'Apr 25',
  score: 78,
  isKeyEvent: false
}, {
  month: 'May 25',
  score: 75,
  isKeyEvent: false
}, {
  month: 'Jun 25',
  score: 72,
  event: 'Financial statement: Revenue growth slowed to 2%',
  isKeyEvent: true
}, {
  month: 'Jul 25',
  score: 68,
  isKeyEvent: false
}, {
  month: 'Aug 25',
  score: 62,
  event: 'News: Key product launch delayed by 6 months',
  isKeyEvent: true
}, {
  month: 'Sep 25',
  score: 55,
  event: 'Financial statement: Revenue declined 8% YoY',
  isKeyEvent: true
}, {
  month: 'Oct 25',
  score: 48,
  event: 'Trade data: Payment terms extended to 45 days',
  isKeyEvent: true
}, {
  month: 'Nov 25',
  score: 42,
  event: 'People: CFO Michael Bolton departed company',
  isKeyEvent: true
}, {
  month: 'Dec 25',
  score: 38,
  event: 'People: VP Sales departed, 20% staff reduction',
  isKeyEvent: true
}, {
  month: 'Jan 26',
  score: 32,
  event: 'News: Major client contract lost to competitor',
  isKeyEvent: true
}, {
  month: 'Feb 26',
  score: 28,
  event: 'Financial statement: Revenue down 35% YoY, negative cash flow',
  isKeyEvent: true
}, {
  month: 'Mar 26',
  score: 28,
  isKeyEvent: false
}];
const healthScoreData = [{
  month: 'Apr 25',
  score: 82
}, {
  month: 'May 25',
  score: 80
}, {
  month: 'Jun 25',
  score: 76
}, {
  month: 'Jul 25',
  score: 72,
  event: 'Financial statement: Margins compressed 5%',
  isKeyEvent: true
}, {
  month: 'Aug 25',
  score: 68
}, {
  month: 'Sep 25',
  score: 62,
  event: 'Financial statement: Working capital concerns',
  isKeyEvent: true
}, {
  month: 'Oct 25',
  score: 55
}, {
  month: 'Nov 25',
  score: 48,
  event: 'Financial statement: Z-Score declined to 1.8',
  isKeyEvent: true
}, {
  month: 'Dec 25',
  score: 42
}, {
  month: 'Jan 26',
  score: 35,
  event: 'Financial statement: Debt-to-equity at 3.2x',
  isKeyEvent: true
}, {
  month: 'Feb 26',
  score: 28
}, {
  month: 'Mar 26',
  score: 28
}];
const paymentScoreData = [{
  month: 'Apr 25',
  score: 85
}, {
  month: 'May 25',
  score: 83
}, {
  month: 'Jun 25',
  score: 80
}, {
  month: 'Jul 25',
  score: 78
}, {
  month: 'Aug 25',
  score: 72
}, {
  month: 'Sep 25',
  score: 65,
  event: 'DBT increased from 15 to 22 days',
  isKeyEvent: true
}, {
  month: 'Oct 25',
  score: 55,
  event: 'DBT increased to 30 days',
  isKeyEvent: true
}, {
  month: 'Nov 25',
  score: 45
}, {
  month: 'Dec 25',
  score: 35,
  event: 'DBT increased to 45 days',
  isKeyEvent: true
}, {
  month: 'Jan 26',
  score: 25,
  event: 'Trade data: Multiple late payments reported',
  isKeyEvent: true
}, {
  month: 'Feb 26',
  score: 20
}, {
  month: 'Mar 26',
  score: 20
}];
function PositiveIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2417">
      <div className="w-1 h-1.5 bg-green-500 rounded-sm" data-id="element-2418" />
      <div className="w-1 h-2.5 bg-green-500 rounded-sm" data-id="element-2419" />
      <div className="w-1 h-3.5 bg-green-500 rounded-sm" data-id="element-2420" />
    </div>;
}
function NegativeIndicator() {
  return <div className="flex items-end gap-0.5 shrink-0 mt-1" data-id="element-2421">
      <div className="w-1 h-1.5 bg-red-500 rounded-sm" data-id="element-2422" />
      <div className="w-1 h-2.5 bg-red-500 rounded-sm" data-id="element-2423" />
      <div className="w-1 h-3.5 bg-red-500 rounded-sm" data-id="element-2424" />
    </div>;
}
function ScoreTooltip({
  active,
  payload,
  label,
  color
}: any) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dotColor = data.score !== undefined ? getDotColor(data.score) : color;
    return <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-4 py-3 max-w-[250px]" data-id="element-2425">
        <div className="flex items-center justify-between mb-2" data-id="element-2426">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2427">
            {label}
          </p>
          <div className="flex items-center gap-1.5" data-id="element-2428">
            <div className="w-2 h-2 rounded-full" style={{
            backgroundColor: dotColor
          }} data-id="element-2429" />
            <span className="text-sm font-bold text-gray-900" data-id="element-2430">
              {payload[0].value}
            </span>
          </div>
        </div>
        {data.event && <p className="text-sm text-gray-700 leading-snug border-t border-gray-100 pt-2 mt-1" data-id="element-2431">
            {data.event}
          </p>}
      </div>;
  }
  return null;
}
function getDotColor(score: number) {
  if (score <= 30) return '#ef4444';
  if (score <= 70) return '#eab308';
  return '#22c55e';
}
const CustomDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  if (payload.isKeyEvent) {
    return <g data-id="element-2432">
        <circle cx={cx} cy={cy} r={12} fill={color} fillOpacity={0.2} className="animate-pulse" data-id="element-2433" />
        <circle cx={cx} cy={cy} r={6} fill="#ffffff" stroke={color} strokeWidth={3} data-id="element-2434" />
      </g>;
  }
  return <circle cx={cx} cy={cy} r={3} fill={color} stroke={color} strokeWidth={2} data-id="element-2435" />;
};
const CustomActiveDot = (props: any) => {
  const {
    cx,
    cy,
    payload
  } = props;
  const color = getDotColor(payload.score);
  return <circle cx={cx} cy={cy} r={8} fill={color} stroke="#fff" strokeWidth={2} data-id="element-2436" />;
};
export function InitechOverviewTab() {
  const [healthExpanded, setHealthExpanded] = useState(true);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-2437">
      {/* Top Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2438">
        {/* Credit Settings */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-1" data-id="element-2439">
          <div className="flex justify-between items-center mb-6" data-id="element-2440">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-2441">Credit Settings</h3>
            <button className="w-8 h-8 bg-indigo-600 hover:bg-indigo-700 rounded-full flex items-center justify-center text-white transition-colors" data-id="element-2442">
              <Edit2 className="w-4 h-4" data-id="element-2443" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-2444">
            <div data-id="element-2445">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2446">
                Credit Limit
              </p>
              <p className="text-sm text-gray-600" data-id="element-2447">$75,000</p>
            </div>
            <div className="relative group" data-id="element-2448">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2449">
                Recommended Limit
              </p>
              <p className="text-sm text-red-600" data-id="element-2450">$0</p>
              <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10" data-id="element-2451">
                Apr 15, 2025: Approved for $75,000 credit
              </div>
            </div>
            <div data-id="element-2452">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2453">
                Outstanding Balance
              </p>
              <p className="text-sm text-red-600 font-semibold" data-id="element-2454">$68,000</p>
            </div>
            <div data-id="element-2455">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2456">
                Utilization
              </p>
              <p className="text-sm text-red-600 font-semibold" data-id="element-2457">91%</p>
            </div>
            <div data-id="element-2458">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2459">
                AP Contact
              </p>
              <p className="text-sm text-gray-600" data-id="element-2460">Milton Waddams</p>
            </div>
            <div data-id="element-2461">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2462">
                Terms
              </p>
              <p className="text-sm text-gray-600" data-id="element-2463">Net 30</p>
            </div>
            <div data-id="element-2464">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2465">
                Company Officer
              </p>
              <p className="text-sm text-gray-600" data-id="element-2466">Bill Lumbergh</p>
            </div>
            <div data-id="element-2467">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2468">
                Date Last Reviewed
              </p>
              <p className="text-sm text-gray-600" data-id="element-2469">2/15/2026</p>
            </div>
            <div data-id="element-2470">
              <p className="text-sm font-semibold text-gray-900 mb-0.5" data-id="element-2471">
                Group
              </p>
              <p className="text-sm text-gray-600" data-id="element-2472">Software</p>
            </div>
          </div>
        </div>

        {/* Pulse Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 lg:col-span-2 flex flex-col" data-id="element-2473">
          <div className="flex items-center gap-2 mb-4" data-id="element-2474">
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2475">Pulse Score</h3>
          </div>
          <div className="flex gap-6 mb-6" data-id="element-2476">
            <div className="bg-red-100 rounded-lg p-4 flex flex-col items-center justify-center min-w-[120px] shrink-0" data-id="element-2477">
              <span className="text-4xl font-bold text-gray-900" data-id="element-2478">28</span>
              <span className="text-sm font-medium text-gray-800 mt-1" data-id="element-2479">
                High Risk
              </span>
            </div>
            <p className="text-gray-600 text-base leading-relaxed pt-2" data-id="element-2480">
              Pulse Score of 28 indicates high risk and should be reviewed.
              Score has deteriorated dramatically over 10 months from 78 in
              April 2025.
            </p>
          </div>
          <div className="flex-1 min-h-[200px] relative" data-id="element-2481">
            <div className="absolute top-0 right-0 flex items-center gap-2 text-xs text-gray-500 z-10" data-id="element-2482">
              <div className="w-3 h-3 bg-black rounded-sm" data-id="element-2483"></div>
              Pulse Score
            </div>
            <ResponsiveContainer width="100%" height="100%" data-id="element-2484">
              <LineChart data={pulseScoreData} margin={{
              top: 20,
              right: 10,
              left: -20,
              bottom: 10
            }} data-id="element-2485">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2486" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} dy={10} data-id="element-2487" />
                <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#6B7280'
              }} data-id="element-2488" />
                <Tooltip content={<ScoreTooltip data-id="element-2490" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2,
                strokeDasharray: '4 4'
              }} data-id="element-2489" />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" data-id="element-2491" />
                <ReferenceLine y={30} stroke="#ef4444" strokeDasharray="4 4" data-id="element-2492" />
                <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2494" />} activeDot={<CustomActiveDot data-id="element-2495" />} data-id="element-2493" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Health Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2496">
        <button onClick={() => setHealthExpanded(!healthExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2497">
          <div className="flex items-center gap-3" data-id="element-2498">
            <HeartPulse className="w-5 h-5 text-gray-700" data-id="element-2499" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2500">Health Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-red-500 bg-white ml-2" data-id="element-2501">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" data-id="element-2502"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2503">28</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2504" />
          </div>
        </button>

        {healthExpanded && <div className="p-5" data-id="element-2505">
            <p className="text-base text-gray-600 mb-6" data-id="element-2506">
              INITECH has a 45.8% probability of insolvency or bankruptcy in the
              next 12 months.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2507">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2508">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2509">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2510">
                  <div className="flex items-start gap-2" data-id="element-2511">
                    <NegativeIndicator data-id="element-2512" />
                    <span className="text-sm text-gray-600" data-id="element-2513">
                      Revenue decline (-35% YoY) with negative EBITDA and cash
                      burn
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2514">
                    <NegativeIndicator data-id="element-2515" />
                    <span className="text-sm text-gray-600" data-id="element-2516">
                      2 CxOs departed in Q4 2025
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2517">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2518">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2519">
                  <div className="flex items-start gap-2" data-id="element-2520">
                    <PositiveIndicator data-id="element-2521" />
                    <span className="text-sm text-gray-600" data-id="element-2522">
                      Long operating history (33 years)
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-2523">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2524">
                  <LineChart data={healthScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2525">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2526" />
                    <XAxis dataKey="month" hide data-id="element-2527" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-2528" />
                    <Tooltip content={<ScoreTooltip color="#ef4444" data-id="element-2530" />} data-id="element-2529" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2532" />} activeDot={<CustomActiveDot data-id="element-2533" />} data-id="element-2531" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Score */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2534">
        <button onClick={() => setPaymentExpanded(!paymentExpanded)} className="w-full p-5 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors border-b border-gray-100" data-id="element-2535">
          <div className="flex items-center gap-3" data-id="element-2536">
            <CreditCard className="w-5 h-5 text-gray-700" data-id="element-2537" />
            <h3 className="text-xl font-bold text-gray-900" data-id="element-2538">Payment Score</h3>
            <div className="flex items-center gap-2 px-3 py-1 rounded-full border-2 border-red-500 bg-white ml-2" data-id="element-2539">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" data-id="element-2540"></div>
              <span className="text-base font-bold text-gray-900" data-id="element-2541">20</span>
            </div>
            <Sparkles className="w-4 h-4 text-indigo-400 ml-1 animate-sparkle cursor-pointer" title="Ask Pulse AI about this score" data-id="element-2542" />
          </div>
        </button>

        {paymentExpanded && <div className="p-5" data-id="element-2543">
            <p className="text-base text-gray-600 mb-6" data-id="element-2544">
              INITECH has a 78.5% probability of late payment, indicating very
              high risk. Payment behavior was strong until September 2025 but
              has since deteriorated significantly.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-2545">
              {/* Negative Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2546">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2547">
                  Negative Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2548">
                  <div className="flex items-start gap-2" data-id="element-2549">
                    <NegativeIndicator data-id="element-2550" />
                    <span className="text-sm text-gray-600" data-id="element-2551">
                      DBT spiked 200% (15 → 45 days) and 15 days past due
                    </span>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-2552">
                    <NegativeIndicator data-id="element-2553" />
                    <span className="text-sm text-gray-600" data-id="element-2554">
                      Using 91% of available credit ($68K/$75K)
                    </span>
                  </div>
                </div>
              </div>

              {/* Positive Factors */}
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-2555">
                <h4 className="text-sm font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100" data-id="element-2556">
                  Positive Impact Factors
                </h4>
                <div className="space-y-3" data-id="element-2557">
                  <div className="flex items-start gap-2" data-id="element-2558">
                    <PositiveIndicator data-id="element-2559" />
                    <span className="text-sm text-gray-600" data-id="element-2560">
                      New interim CFO hired in January 2026
                    </span>
                  </div>
                </div>
              </div>

              {/* Chart */}
              <div className="lg:col-span-2 h-[200px]" data-id="element-2561">
                <ResponsiveContainer width="100%" height="100%" data-id="element-2562">
                  <LineChart data={paymentScoreData} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-2563">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2564" />
                    <XAxis dataKey="month" hide data-id="element-2565" />
                    <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#6B7280'
                }} data-id="element-2566" />
                    <Tooltip content={<ScoreTooltip color="#ef4444" data-id="element-2568" />} data-id="element-2567" />
                    <Line type="monotone" dataKey="score" stroke="#000000" strokeWidth={2} dot={<CustomDot data-id="element-2570" />} activeDot={<CustomActiveDot data-id="element-2571" />} data-id="element-2569" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}